/**
 * Side-effect module that registers every theme bundle the Sorsy storefront
 * ships into the runtime's {@code themeRegistry}. Importing it once, before
 * anything calls {@link getResolverThemes}, is enough — registration is
 * idempotent and the registry is a module-level singleton.
 *
 * <p><strong>Import order is the whole point of this file.</strong>
 * {@code getResolverThemes()} takes a SNAPSHOT of the registry at
 * module-evaluation time, so a theme registered after that call is silently
 * absent: its blocks fall through to the generic ones with nothing in the
 * console to say why. `blockResolver.ts` imports this module above its
 * {@code createBlockResolver} call for that reason, and the import must stay
 * there.
 *
 * <p>This file's absence was the defect it now fixes. `blockResolver.ts`
 * described it precisely — "adding the Sorsy theme means adding a side-effect
 * `registerThemes.ts` … and importing it ABOVE the call below" — but nothing
 * created it, so the registry was empty, {@code themes: getResolverThemes()}
 * resolved to {@code {}}, and the Sorsy theme was built and then never
 * rendered. Every visitor got the generic enquiry card instead of the
 * Sorsy-branded one whose whole job is making the phone number the loudest
 * thing on the page.
 *
 * <p>How to add a theme: add a workspace dep on {@code @akademi/theme-<name>},
 * import its bundle here, call {@code registerTheme('<themeId>', <bundle>)}.
 * The id must match what operators pick in the admin Theme Editor, and what
 * the backend returns as {@code shell.themeId}.
 *
 * <p>Build-time extension themes: `@storefront/extensions` (aliased in
 * `next.config.ts` from {@code STOREFRONT_EXTENSIONS_MODULE}, an empty list by
 * default) may contribute further bundles. They are registered here, after the
 * built-in ones and still inside this module, so they are in the registry
 * before {@code getResolverThemes()} snapshots it. An extension theme id that
 * collides with a built-in id throws at startup.
 */
import { listThemes, registerTheme } from '@akademi/site-builder-runtime';
import { sorsyTheme } from '@commerce/theme-sorsy';
import extensions from '@storefront/extensions';
import { registerExtensionThemes } from '@/src/extensions/register';

// Idempotent — safe to import more than once.
//
// Sorsy ships no bespoke header/footer chrome, so no `header`/`footer`
// resolver is attached here and the runtime's generic chrome is used. Adding
// one later needs no change at any call site — only an extra key on this
// registration, as the LMS portal does for akademi-net and btk.
registerTheme('sorsy', sorsyTheme);

// Everything registered so far is built in (including the runtime's seeded
// `default`), so the snapshot is the collision set for extension themes.
registerExtensionThemes(extensions, registerTheme, listThemes());

export {};
